import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Menu, X, Globe } from 'lucide-react';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const currentLanguage = i18n.language || 'en';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  const navLinks = [
    { label: t('nav.expertise'), href: '#expertise' },
    { label: t('nav.executiveSearch'), href: '#executive-search' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.testimonials'), href: '#testimonials' },
    { label: t('nav.contact'), href: '#contact' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" onClick={(e) => scrollToSection(e, '#hero')} className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/t02jxey4_TAG%20LOGO.JPG" 
                alt="TAG Logo" 
                className="h-16 w-auto relative z-10"
              />
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="px-4 py-2 text-sm font-semibold text-slate-800 hover:text-cyan-700 transition-colors duration-200 relative group"
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-600 to-teal-600 group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>

          {/* CTA Button & Language Selector - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold text-slate-800 hover:text-cyan-700 transition-colors duration-200 border-2 border-gray-200 rounded-lg hover:border-cyan-500"
              >
                <Globe size={18} />
                <span className="text-lg">{currentLanguage === 'en' ? '🇺🇸' : '🇩🇪'}</span>
                <span className="uppercase">{currentLanguage}</span>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border-2 border-gray-200 rounded-lg shadow-xl z-50">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full px-4 py-2 text-left text-sm font-semibold hover:bg-cyan-50 transition-colors flex items-center space-x-2 ${
                      currentLanguage === 'en' ? 'text-cyan-700 bg-cyan-50' : 'text-slate-800'
                    }`}
                  >
                    <span className="text-lg">🇺🇸</span>
                    <span>English</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`w-full px-4 py-2 text-left text-sm font-semibold hover:bg-cyan-50 transition-colors flex items-center space-x-2 ${
                      currentLanguage === 'de' ? 'text-cyan-700 bg-cyan-50' : 'text-slate-800'
                    }`}
                  >
                    <span className="text-lg">🇩🇪</span>
                    <span>Deutsch</span>
                  </button>
                </div>
              )}
            </div>

            <Button 
              onClick={(e) => scrollToSection(e, '#contact')}
              className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-600/40"
            >
              {t('nav.getStarted')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-800 hover:text-cyan-700 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-6 animate-in slide-in-from-top duration-300 bg-white border-t border-gray-200">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="px-4 py-3 text-sm font-semibold text-slate-800 hover:text-cyan-700 hover:bg-cyan-50 rounded-lg transition-all duration-200"
                >
                  {link.label}
                </a>
              ))}
              
              {/* Language Selector - Mobile */}
              <div className="px-4 py-2">
                <div className="flex items-center space-x-2 mb-2">
                  <Globe size={18} className="text-slate-800" />
                  <span className="text-sm font-semibold text-slate-800">Language</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                      currentLanguage === 'en' 
                        ? 'bg-cyan-600 text-white' 
                        : 'bg-gray-100 text-slate-800 hover:bg-cyan-50'
                    }`}
                  >
                    <span className="text-base">🇺🇸</span>
                    <span>English</span>
                  </button>
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                      currentLanguage === 'de' 
                        ? 'bg-cyan-600 text-white' 
                        : 'bg-gray-100 text-slate-800 hover:bg-cyan-50'
                    }`}
                  >
                    <span className="text-base">🇩🇪</span>
                    <span>Deutsch</span>
                  </button>
                </div>
              </div>

              <Button 
                onClick={(e) => scrollToSection(e, '#contact')}
                className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white font-semibold mt-4"
              >
                {t('nav.getStarted')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
